import React from 'react';
import { CloseIcon } from 'assests/GmsIcons';
import { CLEAR_ALL } from '../constants/CategoryRelevancyConstants';

type AppliedFiltersProps = {
    filters: FilterOptions;
    onRemoveFilter: (filterType: keyof FilterOptions | 'all', value?: string) => void;
    onFilterChange?: (newFilters: FilterOptions) => void;
    activeTab?: TabType;
}

const AppliedFilters = ({ filters, onRemoveFilter, onFilterChange, activeTab = 'raw' }: AppliedFiltersProps) => {
    const hasFilters = Object.entries(filters).some(([key, value]) =>
        key !== 'is_curated'
            ? Array.isArray(value) && value.length > 0
            : value !== undefined || (Array.isArray(filters.curatedSelections) && filters.curatedSelections.length > 0)
    );

    if (!hasFilters) {
        return null;
    }

    const hasBothCuratedSelections =
        Array.isArray(filters.curatedSelections) &&
        filters.curatedSelections.includes('active') &&
        filters.curatedSelections.includes('inactive');

    const handleRemoveCuratedSelection = (selection: 'active' | 'inactive') => {
        if (!onFilterChange) {
            onRemoveFilter('is_curated');
            return;
        }

        const newFilters = { ...filters };

        const keepSelection = selection === 'active' ? 'inactive' : 'active';
        newFilters.curatedSelections = [keepSelection];

        newFilters.is_curated = keepSelection === 'active';

        onFilterChange(newFilters);
    };

    const shouldShowCuratedFilters = activeTab === 'raw';

    return (
        <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-4" data-testid="applied-filters">
            {filters?.categories?.map(category => (
                <div
                    key={`category-${category.category_id}`}
                    className="tw-flex tw-items-center tw-bg-gray-100 tw-rounded-md tw-px-3 tw-py-1"
                    data-testid={`filter-category-${category.category_id}`}
                >
                    <span className="tw-mr-1">Category: {category.category_name}</span>
                    <button
                        className="tw-text-gray-500 hover:tw-text-gray-700"
                        onClick={() => onRemoveFilter('categories', category.category_id)}
                        aria-label={`Remove Category: ${category.category_name}`}
                    >
                        <CloseIcon />
                    </button>
                </div>
            ))}

            {filters?.category_types?.map(categoryType => (
                <div
                    key={`category-type-${categoryType.category_type_id}`}
                    className="tw-flex tw-items-center tw-bg-gray-100 tw-rounded-md tw-px-3 tw-py-1"
                    data-testid={`filter-category-type-${categoryType.category_type_id}`}
                >
                    <span className="tw-mr-1">Category Type: {categoryType.category_type_name}</span>
                    <button
                        className="tw-text-gray-500 hover:tw-text-gray-700"
                        onClick={() => onRemoveFilter('category_types', categoryType.category_type_id)}
                        aria-label={`Remove Category Type: ${categoryType.category_type_name}`}
                    >
                        <CloseIcon />
                    </button>
                </div>
            ))}

            {filters?.co_sold_categories?.map(category => (
                <div
                    key={`co-sold-category-${category.category_id}`}
                    className="tw-flex tw-items-center tw-bg-gray-100 tw-rounded-md tw-px-3 tw-py-1"
                    data-testid={`filter-co-sold-category-${category.category_id}`}
                >
                    <span className="tw-mr-1">Co-sold Category: {category.category_name}</span>
                    <button
                        className="tw-text-gray-500 hover:tw-text-gray-700"
                        onClick={() => onRemoveFilter('co_sold_categories', category.category_id)}
                        aria-label={`Remove Co-sold Category: ${category.category_name}`}
                    >
                        <CloseIcon />
                    </button>
                </div>
            ))}

            {filters?.co_sold_category_types?.map(categoryType => (
                <div
                    key={`co-sold-category-type-${categoryType.category_type_id}`}
                    className="tw-flex tw-items-center tw-bg-gray-100 tw-rounded-md tw-px-3 tw-py-1"
                    data-testid={`filter-co-sold-category-type-${categoryType.category_type_id}`}
                >
                    <span className="tw-mr-1">Co-sold Category Type: {categoryType.category_type_name}</span>
                    <button
                        className="tw-text-gray-500 hover:tw-text-gray-700"
                        onClick={() => onRemoveFilter('co_sold_category_types', categoryType.category_type_id)}
                        aria-label={`Remove Co-sold Category Type: ${categoryType.category_type_name}`}
                    >
                        <CloseIcon />
                    </button>
                </div>
            ))}

            {shouldShowCuratedFilters && hasBothCuratedSelections ? (
                <>
                    <div
                        className="tw-flex tw-items-center tw-bg-gray-100 tw-rounded-md tw-px-3 tw-py-1"
                        data-testid="filter-curated-active"
                    >
                        <span className="tw-mr-1">Curated: Active</span>
                        <button
                            className="tw-text-gray-500 hover:tw-text-gray-700"
                            onClick={() => handleRemoveCuratedSelection('active')}
                            data-testid="remove-curated-active"
                            aria-label="Remove Curated: Active filter"
                        >
                            <CloseIcon />
                        </button>
                    </div>
                    <div
                        className="tw-flex tw-items-center tw-bg-gray-100 tw-rounded-md tw-px-3 tw-py-1"
                        data-testid="filter-curated-inactive"
                    >
                        <span className="tw-mr-1">Curated: Inactive</span>
                        <button
                            className="tw-text-gray-500 hover:tw-text-gray-700"
                            onClick={() => handleRemoveCuratedSelection('inactive')}
                            data-testid="remove-curated-inactive"
                            aria-label="Remove Curated: Inactive filter"
                        >
                            <CloseIcon />
                        </button>
                    </div>
                </>
            ) : (
                shouldShowCuratedFilters && filters?.is_curated !== undefined && (
                    <div
                        className="tw-flex tw-items-center tw-bg-gray-100 tw-rounded-md tw-px-3 tw-py-1"
                        data-testid={`filter-curated-${filters.is_curated ? 'active' : 'inactive'}`}
                    >
                        <span className="tw-mr-1">Curated: {filters.is_curated ? 'Active' : 'Inactive'}</span>
                        <button
                            className="tw-text-gray-500 hover:tw-text-gray-700"
                            onClick={() => onRemoveFilter('is_curated')}
                            aria-label={`Remove Curated: ${filters.is_curated ? 'Active' : 'Inactive'} filter`}
                        >
                            <CloseIcon />
                        </button>
                    </div>
                )
            )}

            {hasFilters && (
                <button
                    className="tw-text-blue-600 hover:tw-underline"
                    onClick={() => onRemoveFilter('all')}
                    data-testid="clear-all-filters"
                >
                    {CLEAR_ALL}
                </button>
            )}
        </div>
    );
};

export default AppliedFilters;